import { addrKey, trNormalize } from "./text";

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);

function tokenize(s) {
  return addrKey(s).split(" ").filter(Boolean);
}
function jaccard(aTokens, bTokens) {
  const A = new Set(aTokens);
  const B = new Set(bTokens);
  const inter = [...A].filter((x) => B.has(x)).length;
  const union = new Set([...A, ...B]).size || 1;
  return inter / union;
}

/**
 * Mobility satırlarını normalize eder.
 * ÖNEMLİ: dataset.type'a güvenmez; her dataset'i tarar,
 * 'Adres' + 'Tekil Kullanıcı' benzeri kolon bulunursa mobilite olarak işler.
 */
export function normalizeMobilityRows(datasets) {
  const out = [];

  (datasets || []).forEach((dataset) => {
    const rows = dataset?.rows || [];
    if (!rows.length) return;

    // kolonları normalize et
    const cols = Object.keys(rows[0]);
    const ncols = cols.map((c) => trNormalize(c));

    const idxAdres =
      ncols.findIndex((c) => c.includes("adres")) >= 0
        ? ncols.findIndex((c) => c.includes("adres"))
        : ncols.findIndex((c) => c.includes("unnamed"));

    const hasUsersHint = ncols.some(
      (c) => c.includes("tekil") || c.includes("kullanici") || c === "users"
    );

    // mobilite sinyali yoksa es geç
    if (idxAdres < 0 || !hasUsersHint) return;

    const adresCol = cols[idxAdres];

    rows.forEach((r) => {
      const rawAddr = r[adresCol];
      if (!rawAddr) return;
      const addrStr = String(rawAddr).trim();
      // başlık satırını at
      if (addrStr.toLowerCase() === "adres") return;

      const address = addrStr;
      const addressKey = addrKey(address);
      const tokens = tokenize(address);

      const city =
        r.IL ?? r["İl"] ?? r.SEHIR ?? r.SEHİR ?? r["Şehir"] ?? r.City ?? "";

      const users =
        num(r["Tekil Kullanıcı Sayısı"]) ??
        num(r["Tekil Kullanıcı"]) ??
        num(r["TEKIL KULLANICI SAYISI"]) ?? // olası varyant
        num(r.Users) ??
        0;

      // 0..1 aralığındaki yüzdelik/pay alanlarını yakala
      const shares = {};
      Object.entries(r).forEach(([k, v]) => {
        const n = Number(v);
        // Filtreyi kaldırdık, sadece 0..1 aralığı kontrolü var
        if (Number.isFinite(n) && n >= 0 && n <= 1) {
          shares[k] = n;
        }
      });
      out.push({
        address,
        addressKey,
        _tokens: tokens,
        city,
        users: users || 0,
        shares,
        __dataset: dataset.name,
      });
    });
  });

  return out;
}

/** site.address ile mobility.address eşleşmesi: tam → içerme → fuzzy (Jaccard) */
export function matchMobilityByAddress(site, mobRows) {
  const key = addrKey(site.address || site.name || "");
  const siteTokens = tokenize(site.address || site.name || "");
  if (!key && siteTokens.length === 0) return [];

  // 1) tam
  let rows = mobRows.filter((r) => r.addressKey === key);
  if (rows.length) return rows;

  // 2) içerme
  rows = mobRows.filter((r) => r.addressKey.includes(key) || key.includes(r.addressKey));
  if (rows.length) return rows;

  // 3) fuzzy
  const scored = mobRows
    .map((r) => ({ row: r, score: jaccard(siteTokens, r._tokens) }))
    .filter((x) => x.score >= 0.45)
    .sort((a, b) => b.score - a.score);

  if (scored.length === 0) return [];
  const topScore = scored[0].score;
  const band = scored.filter((x) => x.score >= topScore - 0.05).map((x) => x.row);
  return band;
}

/** Ağırlıklı paylar + toplam kullanıcı + top-10 adres */
export function aggregateMobilityRows(rows) {
  const totalUsers = rows.reduce((s, r) => s + (Number(r.users) || 0), 0);

  const wavg = {};
  const sumW = totalUsers || 1;
  rows.forEach((r) => {
    const w = Number(r.users) || 0;
    Object.entries(r.shares || {}).forEach(([k, v]) => {
      if (!Number.isFinite(v)) return;
      wavg[k] = (wavg[k] || 0) + v * w;
    });
  });
  Object.keys(wavg).forEach((k) => (wavg[k] = wavg[k] / sumW));

  const topPoints = [...rows]
    .map((r) => ({ address: r.address || "-", users: Number(r.users) || 0 }))
    .sort((a, b) => b.users - a.users)
    .slice(0, 10);

  return { totalUsers, shares: wavg, topPoints };
}

export function filterByCities(rows, cities) {
  const wanted = new Set((cities || []).map(trNormalize));
  if (!wanted.size) return [];
  return rows.filter((r) => wanted.has(trNormalize(r.city || "")));
}
